// Pure helpers for the Superadmin "Speech provider" UI. No imports so they run under `deno test`.

export type SpeechPresetKey = 'groq' | 'openai' | 'openrouter' | 'custom';

export const SPEECH_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
];

export interface VoiceEntry { model: string; voice: string }
export type VoiceDraft = Record<string, VoiceEntry>;

// Model ids and voices are suggestions only: the Test button proves what the provider actually accepts.
// Groq's text-to-speech models change (PlayAI was retired in Dec 2025); confirm ids and voice names against
// the provider's docs and the live Test result, and note Groq may ask you to accept a model's terms in its console first.
export const SPEECH_PRESETS: Record<SpeechPresetKey, { label: string; baseUrl: string; suggested: VoiceDraft; note: string }> = {
  groq: {
    label: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    suggested: {
      en: { model: 'canopylabs/orpheus-v1-english', voice: 'hannah' },
      ar: { model: 'canopylabs/orpheus-arabic-saudi', voice: '' },
    },
    note: 'English voices are US-style; Arabic is Saudi. The Arabic model is a paid preview. Enter the Arabic voice name from the Groq docs, then run Test: Groq may need the model\'s terms accepted in the Groq console first.',
  },
  openai: {
    label: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    suggested: {
      en: { model: 'gpt-4o-mini-tts', voice: 'alloy' },
      ar: { model: 'gpt-4o-mini-tts', voice: 'alloy' },
      hi: { model: 'gpt-4o-mini-tts', voice: 'alloy' },
    },
    note: 'OpenAI voices are multilingual. A per-voice accent instruction is a later stage.',
  },
  openrouter: {
    label: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    suggested: {
      en: { model: 'google/gemini-3.8-flash-tts', voice: 'Kore' },
      ar: { model: 'google/gemini-3.8-flash-tts', voice: 'Kore' },
      hi: { model: 'google/gemini-3.8-flash-tts', voice: 'Kore' },
    },
    note: 'One OpenRouter key reaches many speech models, and each language can use a different model and voice. Ideas: Gemini TTS for multilingual (Arabic, Hindi), microsoft/mai-voice-2.1 with voice en-IN-Priya:MAI-Voice-2.1 for an Indian-English voice and hi-IN-Priya:MAI-Voice-2.1 for Hindi, elevenlabs/eleven-multilingual-v2 for premium quality. Voice names differ per model; Test shows what the provider accepts. This needs its own OpenRouter key (the chat provider key is separate).',
  },
  custom: { label: 'Custom OpenAI-compatible', baseUrl: '', suggested: {}, note: '' },
};

export const CUSTOM_SPEECH_NOTE =
  'The endpoint must implement POST {base}/audio/speech (OpenAI-compatible) with { model, voice, input, response_format } (mp3, or wav for Groq) and return audio. ' +
  'A self-hosted server (for example Kokoro-FastAPI or a Piper wrapper) that does not check keys still needs any non-empty placeholder key here. ' +
  'The server must be reachable over https, or http on localhost / host.docker.internal.';

export interface SpeechProvider {
  id: string;
  label: string;
  preset: string;
  base_url: string;
  default_model: string;
  api_key_last4: string | null;
  is_active: boolean;
  health_status: 'unknown' | 'healthy' | 'unhealthy';
  health_error: string | null;
  health_checked_at: string | null;
  speech_config: VoiceDraft | null;
}

export interface SpeechLanguageResult { language: string; ok: boolean; error?: string }

export interface SpeechTestResult {
  ok: boolean;
  httpStatus?: number;
  latencyMs?: number;
  error?: string;
  errorCode?: string;
  languages?: SpeechLanguageResult[];
  testToken?: string;
}

/** What the test was run against. The apiKey is only held in memory to detect edits. */
export interface TestedSpeechConfig { baseUrl: string; apiKey: string; voices: VoiceDraft }

/** Entries with both a model and a voice, trimmed; blank rows mean "no cloud voice for that language". */
export function mappedVoices(d: VoiceDraft): VoiceDraft {
  const out: VoiceDraft = {};
  for (const [lang, raw] of Object.entries(d)) {
    // Stored JSON can hold anything: skip entries that are not { model: string, voice: string }.
    const v = raw as unknown;
    if (!v || typeof v !== 'object') continue;
    const { model: m, voice: vo } = v as Record<string, unknown>;
    if (typeof m !== 'string' || typeof vo !== 'string') continue;
    const model = m.trim();
    const voice = vo.trim();
    if (model && voice) out[lang] = { model, voice };
  }
  return out;
}

export function firstSpeechModel(d: VoiceDraft): string {
  return Object.values(mappedVoices(d))[0]?.model ?? '';
}

function voicesKey(d: VoiceDraft): string {
  return JSON.stringify(Object.entries(mappedVoices(d)).sort(([a], [b]) => a.localeCompare(b)));
}

export function sameSpeechConfig(a: TestedSpeechConfig, b: TestedSpeechConfig): boolean {
  return a.baseUrl.trim() === b.baseUrl.trim() && a.apiKey === b.apiKey && voicesKey(a.voices) === voicesKey(b.voices);
}

export function canTestSpeech(o: { baseUrl: string; apiKey: string; voices: VoiceDraft; isEdit: boolean; keyReentry: boolean }): boolean {
  if (!o.baseUrl.trim() || Object.keys(mappedVoices(o.voices)).length === 0) return false;
  if (o.keyReentry) return false;
  return o.isEdit || o.apiKey.length > 0;
}

export function canSaveSpeech(o: {
  label: string; test: { result: SpeechTestResult; config: TestedSpeechConfig } | null; current: TestedSpeechConfig;
  saving: boolean; testing: boolean; keyReentry: boolean;
}): boolean {
  if (o.saving || o.testing || o.keyReentry || o.label.trim().length === 0) return false;
  return !!o.test && o.test.result.ok === true && !!o.test.result.testToken && sameSpeechConfig(o.test.config, o.current);
}

const langLabel = (code: string) => SPEECH_LANGUAGES.find((l) => l.code === code)?.label ?? code;

export function describeSpeechTest(r: SpeechTestResult): { ok: boolean; title: string; detail?: string; raw?: string } {
  if (r.ok) {
    const names = (r.languages ?? []).map((l) => langLabel(l.language)).join(', ');
    return {
      ok: true,
      title: 'Passed: every mapped language produced audio',
      detail: [names, r.latencyMs != null ? `${r.latencyMs} ms` : ''].filter(Boolean).join(' · ') || undefined,
    };
  }
  const failed = (r.languages ?? []).filter((l) => !l.ok);
  const raw = failed.map((l) => `${l.language}: ${l.error ?? 'failed'}`).join('\n') || r.error;
  const which = failed.length ? `Failed for: ${failed.map((l) => langLabel(l.language)).join(', ')}.` : undefined;
  switch (r.errorCode) {
    case 'http_error':
      if (r.httpStatus === 401 || r.httpStatus === 403) {
        return { ok: false, title: 'Authentication failed', detail: `The provider rejected the API key (HTTP ${r.httpStatus}). Check the key and that it can use text-to-speech.`, raw };
      }
      if (r.httpStatus === 404) {
        return { ok: false, title: 'Endpoint or model not found', detail: [which, 'Check the base URL (it should end in /v1) and the model names.'].filter(Boolean).join(' '), raw };
      }
      if (r.httpStatus === 429) {
        return { ok: false, title: 'Rate limited or out of quota', detail: 'The provider returned HTTP 429. Try again shortly or check the account quota.', raw };
      }
      return { ok: false, title: `Provider returned HTTP ${r.httpStatus ?? 'error'}`, detail: [which, 'A voice or model name may be wrong; see Details.'].filter(Boolean).join(' '), raw };
    case 'unreachable':
      return { ok: false, title: 'Could not reach the provider', detail: 'Network error: check the base URL and that the endpoint is reachable from the server.', raw };
    case 'bad_response':
      return { ok: false, title: 'Unexpected response', detail: 'The endpoint did not return audio.', raw };
    case 'no_languages':
      return { ok: false, title: 'No language mapped', detail: 'Give at least one language a model and a voice.' };
    default:
      return { ok: false, title: 'Test failed', raw };
  }
}

/** Message for an error returned by save / activate / deactivate / delete. */
export function describeSpeechError(code?: string, message?: string): string {
  switch (code) {
    case 'test_required': return 'The connection test is missing or out of date. Run Test again for the current URL, languages and key, then save.';
    case 'key_reentry_required': return 'Re-enter the API key to use a different base URL';
    case 'kind_mismatch': return 'That provider is not a speech provider.';
    case 'platform_only': return 'Speech providers are platform-wide only.';
    case 'invalid_speech_config': return 'Map at least one language to a model and a voice (language codes such as en or ar).';
    case 'invalid_base_url': return message && message.length > 0 ? message : 'The base URL is not allowed.';
    default: return message && message.length > 0 ? message : 'Something went wrong. Please try again.';
  }
}

export function canActivateSpeech(p: SpeechProvider): boolean {
  return !p.is_active && p.health_status === 'healthy';
}

export function speechActivateBlockedReason(p: SpeechProvider): string | null {
  if (p.is_active) return 'Already active';
  return canActivateSpeech(p) ? null : 'Run Test first: activation requires a passing speech test';
}

export function speechHealthText(p: SpeechProvider): string {
  return p.health_status === 'healthy' ? 'healthy' : p.health_status === 'unhealthy' ? 'unhealthy' : 'not tested';
}

export const NO_ACTIVE_SPEECH_BANNER =
  'No speech provider is active. Ask Masira reads replies with each browser\'s on-device voices. ' +
  'Add and activate a provider, then tenant admins can switch on Cloud voice in Tenant Settings.';
